import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorMessage } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { WBSButton } from '@/common/WBSButton';
import { FollowUpNotes } from '@/follow-up/FollowUpNotes';
import { FollowUpStudyInfo } from '@/follow-up/followUpStudyInfo';
import {
  SectionHeading,
  ViewBody,
  ViewContainer,
  ViewHeader,
  ViewHeaderTitle,
} from '@/follow-up/followUpStyles';
import { PendingRequestContactInfo } from '@/follow-up/PendingRequestContactInfo';
import { StudentLocationNotice } from '@/follow-up/StudentLocationNotice';
import { AvatarNameCell } from '@/tables/AvatarNameCell';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { PageContainer } from '~/styles/boardStyles';
import { formatLongDate } from '~/utils/dateFormat';
import { formatString } from '~/utils/stringFormat';

export const Route = createFileRoute(
  '/_app/follow-ups/pending-requests/view/$followUpId',
)({
  component: PartnerPendingFollowUpScreen,
});

/**
 * The partner's accept/reject view.
 *
 * Not the shared detail: its path id is a follow-up, not a request, and
 * legacy's coordinator version of this page was a stub, so the route is
 * partner-only.
 *
 * "Requested On" here is the follow-up's own date, `partner_assigned_at`, not
 * the `requested_on` field, which is the request's. The two can differ by more
 * than a year.
 */
function PartnerPendingFollowUpScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { followUpId } = Route.useParams();
  const id = Number(followUpId);
  const { me, isPending: mePending } = useCurrentUser();

  const [rejectReason, setRejectReason] = useState('');

  const {
    data: detail,
    isLoading,
    isError,
  } = api.getFollowUp.useQuery({ path: { follow_up_id: id } });

  const {
    mutateAsync: accept,
    isPending: accepting,
    error: acceptError,
  } = api.acceptFollowUp.useMutation();
  const {
    mutateAsync: reject,
    isPending: rejecting,
    error: rejectError,
  } = api.rejectFollowUp.useMutation();

  const backToQueue = () =>
    void navigate({ to: '/follow-ups/pending-requests/' });

  const afterDecision = async (): Promise<void> => {
    await Promise.all([
      api.listPartnerPendingFollowUps.invalidateQueries(),
      // Accept moves the row onto the partner's entries tab; reject does not,
      // but invalidating both is one refetch either way.
      api.listPartnerFollowUps.invalidateQueries(),
      api.getFollowUp.invalidateQueries(),
    ]);
    // Back to the queue this row was opened from, as legacy redirects.
    backToQueue();
  };

  const handleAccept = async (): Promise<void> => {
    try {
      await accept({ path: { follow_up_id: id } });
    } catch {
      return;
    }
    await afterDecision();
  };

  const handleReject = async (): Promise<void> => {
    try {
      await reject({
        path: { follow_up_id: id },
        body: { message_for_coordinator: rejectReason },
      });
    } catch {
      return;
    }
    await afterDecision();
  };

  // Defence in depth: `get_follow_up` already 404s anyone outside the row's
  // viewers, and only the invited partner gets `can_accept_or_reject`. No admin
  // arm: this is the partner's own queue.
  if (mePending || isLoading) return null;
  if (me?.profile_type !== 'FOLLOWUP_PARTNER') return <NotFoundScreen />;
  if (isError || detail === undefined) return <NotFoundScreen />;

  const writeError = acceptError ?? rejectError;

  return (
    <PageContainer>
      <ViewContainer>
        <LargeArrowBackButton onClick={backToQueue} />
        <ViewHeader>
          <ViewHeaderTitle>
            {formatString(t('wbs.follow-up-s'), String(detail.id))}
          </ViewHeaderTitle>
        </ViewHeader>
        <ViewBody>
          <Columns>
            <Column>
              <Section>
                <SectionHeading>
                  {t('lessons.student-s-contact-info')}
                </SectionHeading>
                <AvatarNameCell
                  avatar={detail.student?.avatar ?? null}
                  name={detail.student?.display_name ?? ''}
                  linkUserId={detail.student?.id}
                />
                {/* The follow-up's stored contact snapshot. */}
                <PendingRequestContactInfo
                  contact={detail.student_contact_info}
                />
              </Section>
              <Section>
                <SectionHeading>
                  {t('lessons.student-s-study-info')}
                </SectionHeading>
                {/* Live study progress, not the snapshot, as legacy
                   overwrote the snapshot with live data. The endpoint
                   resolves it. */}
                <FollowUpStudyInfo
                  progress={detail.student_study_progress}
                  progressLabel={detail.study_progress_label}
                />
              </Section>
            </Column>

            <Column>
              <Section>
                <SectionHeading>{t('wbs.requested-by')}</SectionHeading>
                <AvatarNameCell
                  avatar={detail.coordinator?.avatar ?? null}
                  name={detail.coordinator?.display_name ?? ''}
                  linkUserId={detail.coordinator?.id}
                />
              </Section>
              <Section>
                <SectionHeading>{t('wbs.requested-on')}</SectionHeading>
                {/* The follow-up's own date; see the docstring. */}
                <div>{formatLongDate(detail.partner_assigned_at)}</div>
              </Section>
              <Section>
                <SectionHeading>{t('wbs.selected-requests')}</SectionHeading>
                <ObjectiveList>
                  {detail.selected_requests.map((option) => (
                    <li key={option.value}>{option.label}</li>
                  ))}
                </ObjectiveList>
              </Section>
              {/* `wbs.no-message-2` is "No message" without a period;
                 `wbs.no-message` is a different string with different
                 translations. */}
              <FollowUpNotes
                title={t('wbs.follow-up-coordinator-s-message')}
                message={detail.message_for_partner || t('wbs.no-message-2')}
                $smallTitle
              />
            </Column>
          </Columns>

          {/* The map isn't built, so the notice legacy showed only when
             geocoding failed always renders. */}
          <StudentLocationNotice />

          {writeError != null && (
            <AlertBox variant="error">
              {errorMessage(
                writeError,
                t('wbs.there-was-a-problem-with-your-submission'),
              )}
            </AlertBox>
          )}

          {/* The textarea renders whatever the status; only the two buttons
             are gated. */}
          <Section>
            <SectionHeading>
              {t('wbs.message-for-follow-up-coordinator')}
            </SectionHeading>
            <RejectReason
              value={rejectReason}
              onChange={(event) => setRejectReason(event.target.value)}
              rows={4}
              data-testid="follow-up-reject-reason"
            />
          </Section>

          {/* Reject and Accept only while the invitation is pending and the
             caller is the invited partner. A partner reaching an
             already-decided row sees no buttons; the 409 is a backstop. */}
          {detail.can_accept_or_reject && (
            <Actions>
              <WBSButton
                alternate
                type="button"
                onClick={() => void handleReject()}
                isLoading={rejecting}
                data-testid="reject-follow-up"
              >
                {t('wbs.reject')}
              </WBSButton>
              <WBSButton
                type="button"
                onClick={() => void handleAccept()}
                isLoading={accepting}
                data-testid="accept-follow-up"
              >
                {t('wbs.accept')}
              </WBSButton>
            </Actions>
          )}
        </ViewBody>
      </ViewContainer>
    </PageContainer>
  );
}

const Columns = styled.div`
  display: grid;
  gap: 32px;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ObjectiveList = styled.ul`
  font-size: 14px;
  margin: 0;
  padding-left: 20px;
`;

const RejectReason = styled.textarea`
  background: var(--grey-100);
  border: none;
  border-radius: 6px;
  box-shadow: var(--shadow-inset-md);
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  outline: none;
  padding: 12px 14px;
  resize: vertical;
  width: 100%;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;
